import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { eventsApi, authApi, Event, OrganizerProfile } from '../api/client';
import EventManager from '../components/EventManager';
import AttendeeManager from '../components/AttendeeManager';
import CreateEventModal from '../components/CreateEventModal';
import Logo from '../components/Logo';
import LoadingSpinner from '../components/LoadingSpinner';
import { formatDate } from '../utils/datetime';
import { isAuthError } from '../utils/errorHandler';
import { PlusIcon, ArrowRightOnRectangleIcon, Cog6ToothIcon, UserGroupIcon, UserIcon, UsersIcon, QrCodeIcon, EnvelopeIcon, StarIcon } from '@heroicons/react/24/outline';
import Dropdown from '../components/ui/Dropdown';

const OrganizerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentUser, setCurrentUser] = useState<OrganizerProfile | null>(null);
  const [eventTypeFilter, setEventTypeFilter] = useState<'all' | 'social' | 'meetup' | 'conference' | 'casual'>('all');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showPastEvents, setShowPastEvents] = useState(false);

  // Handle Google OAuth token from URL
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      // Store token in localStorage for API requests
      localStorage.setItem('token', token);
      // Clean up URL by removing token parameter
      searchParams.delete('token');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  const loadEvents = async () => {
    try {
      const data = await eventsApi.getOrganizerEvents();
      setEvents(data);
      // Select first active event if no event is selected
      if (!selectedEvent) {
        const activeEvents = data.filter(e => e.status === 'active');
        if (activeEvents.length > 0) {
          setSelectedEvent(activeEvents[0]);
        }
      }
    } catch (error) {
      console.error('Failed to load events:', error);
      // Don't set loading to false here - let the main effect handle it
      throw error; // Re-throw to let the main effect handle it
    }
  };


  useEffect(() => {
    // Check for authentication before loading data
    const checkAuthAndLoad = async () => {
      try {
        // Try to load profile first - if this fails, user is not authenticated
        const profile = await authApi.getProfile();
        setCurrentUser(profile);
        // If profile loads successfully, load events
        await loadEvents();
      } catch (error) {
        // If authentication fails, redirect to login immediately
        if (isAuthError(error)) {
          navigate('/login');
          return;
        }
        console.error('Failed to load profile:', error);
      } finally {
        // Always set loading to false
        setLoading(false);
      }
    };

    checkAuthAndLoad();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Clear local data and redirect
    localStorage.removeItem('organizer');
    navigate('/');
  };

  const handleEventCreated = (newEvent: Event) => {
    setEvents([...events, newEvent]);
    setSelectedEvent(newEvent);
    setShowCreateModal(false);
  };

  const handleEventUpdated = (updatedEvent: Event) => {
    setEvents(events.map(e => e.id === updatedEvent.id ? updatedEvent : e));
    setSelectedEvent(updatedEvent);
  };

  const handleEventDeleted = (eventId: number) => {
    setEvents(events.filter(e => e.id !== eventId));
    if (selectedEvent?.id === eventId) {
      setSelectedEvent(events.length > 1 ? events[0] : null);
    }
  };

  const handleEventArchived = async (eventId: number) => {
    try {
      await eventsApi.archiveEvent(eventId);
      loadEvents();
    } catch (error) {
      console.error('Failed to archive event:', error);
    }
  };

  const handleEventRestored = async (eventId: number) => {
    try {
      await eventsApi.restoreEvent(eventId);
      loadEvents();
    } catch (error) {
      console.error('Failed to restore event:', error);
    }
  };

  // Helper function to get event type badge style
  const getEventTypeBadgeStyle = (type: string) => {
    switch (type) {
      case 'social':
        return 'bg-purple-100 text-purple-800';
      case 'meetup':
        return 'bg-blue-100 text-blue-800';
      case 'conference':
        return 'bg-green-100 text-green-800';
      case 'casual':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Filter events by type
  const filteredEvents = eventTypeFilter === 'all' 
    ? events 
    : events.filter(e => e.event_type === eventTypeFilter);

  // Group events by status for display
  const now = new Date();
  const draftEvents = filteredEvents.filter(e => e.status === 'draft');

  // Active events should be split into upcoming and past based on date
  const allActiveEvents = filteredEvents.filter(e => e.status === 'active');
  const upcomingActiveEvents = allActiveEvents.filter(e => new Date(e.date_time) >= now);
  const pastActiveEvents = allActiveEvents.filter(e => new Date(e.date_time) < now);

  // Combine past active events with archived events for the "past" section
  const archivedEvents = filteredEvents.filter(e => e.status === 'archived');
  const allPastEvents = [...pastActiveEvents, ...archivedEvents];

  // For display purposes, show only upcoming active events in the main section
  const activeEvents = showPastEvents ? allActiveEvents : upcomingActiveEvents;

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Logo className="mr-4" size="medium" />
              <h1 className="text-xl font-semibold">Event Dashboard</h1>
            </div>
            <div className="flex items-center gap-2">
              {/* Settings Dropdown with all options */}
              <Dropdown
                align="right"
                trigger={
                  <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors">
                    <Cog6ToothIcon className="h-5 w-5" />
                  </button>
                }
                items={[
                  {
                    label: 'QR Scanner',
                    onClick: () => navigate('/organizer/scanner'),
                    icon: <QrCodeIcon className="h-4 w-4" />,
                    className: 'text-blue-600 hover:bg-blue-50'
                  },
                  { divider: true },
                  {
                    label: 'Profile',
                    onClick: () => navigate('/profile'),
                    icon: <UserIcon className="h-4 w-4" />
                  },
                  ...(currentUser && currentUser.role !== 'organizer' ? [{
                    label: 'Team Management',
                    onClick: () => navigate('/organizers'),
                    icon: <UsersIcon className="h-4 w-4" />
                  }] : []),
                  {
                    label: 'Sponsors',
                    onClick: () => navigate('/organizers/sponsors'),
                    icon: <StarIcon className="h-4 w-4" />
                  },
                  {
                    label: 'Settings',
                    onClick: () => navigate('/settings'),
                    icon: <Cog6ToothIcon className="h-4 w-4" />
                  },
                  { divider: true },
                  {
                    label: 'Logout',
                    onClick: handleLogout,
                    icon: <ArrowRightOnRectangleIcon className="h-4 w-4" />,
                    className: 'text-red-600 hover:bg-red-50'
                  }
                ]}
              />
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Events List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b">
                <div className="flex justify-between items-center mb-3">
                  <h2 className="text-lg font-medium">Your Events</h2>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    New Event
                  </button>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600">Filter:</label>
                    <select
                      value={eventTypeFilter}
                      onChange={(e) => setEventTypeFilter(e.target.value as any)}
                      className="text-sm px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="all">All Types</option>
                      <option value="social">Social</option>
                      <option value="meetup">Meetup</option>
                      <option value="conference">Conference</option>
                      <option value="casual">Casual</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label htmlFor="show-past" className="text-sm text-gray-600 cursor-pointer">
                      Past events
                    </label>
                    <button
                      id="show-past"
                      type="button"
                      role="switch"
                      aria-checked={showPastEvents}
                      onClick={() => setShowPastEvents(!showPastEvents)}
                      className={`
                        relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                        transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                        ${showPastEvents ? 'bg-blue-600' : 'bg-gray-200'}
                      `}
                    >
                      <span
                        className={`
                          pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0
                          transition duration-200 ease-in-out
                          ${showPastEvents ? 'translate-x-4' : 'translate-x-0'}
                        `}
                      />
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="divide-y">
                {events.length === 0 ? (
                  <p className="p-4 text-gray-500 text-center">
                    No events yet. Create your first event!
                  </p>
                ) : (
                  <>
                    {/* Draft Events */}
                    {draftEvents.length > 0 && (
                      <>
                        <div className="px-4 py-2 bg-yellow-50 text-sm font-medium text-yellow-800">
                          Draft Events
                        </div>
                        {draftEvents.map((event) => (
                      <button
                        key={event.id}
                        onClick={() => setSelectedEvent(event)}
                        className={`w-full text-left p-4 hover:bg-gray-50 transition ${
                          selectedEvent?.id === event.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-medium">{event.title}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-sm text-gray-600">
                                {formatDate(event.date_time)}
                              </p>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getEventTypeBadgeStyle(event.event_type)}`}>
                                {event.event_type.charAt(0).toUpperCase() + event.event_type.slice(1)}
                              </span>
                            </div>
                          </div>
                          {event.status === 'draft' && (
                            <span className="ml-2 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                              Draft
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-3 mt-2 text-xs">
                          <span className="flex items-center gap-1 text-green-600">
                            <UserGroupIcon className="h-4 w-4" />
                            {event.approved_count} approved
                          </span>
                          <span className="text-yellow-600">
                            {event.pending_count} pending
                          </span>
                          {event.rejected_count > 0 && (
                            <span className="text-red-600">
                              {event.rejected_count} rejected
                            </span>
                          )}
                        </div>
                        {event.invitation_sent_count > 0 && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                            <EnvelopeIcon className="h-4 w-4" />
                            {event.invitation_sent_count} invitations sent
                          </div>
                        )}
                        <div className="mt-2">
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>Capacity</span>
                            <span>{event.capacity_percentage}% full</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div 
                              className={`h-1.5 rounded-full ${
                                event.capacity_percentage >= 90 ? 'bg-red-600' : 
                                event.capacity_percentage >= 70 ? 'bg-yellow-600' : 
                                'bg-green-600'
                              } w-[${Math.min(event.capacity_percentage, 100)}%]`}
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {event.available_spots} spots available
                          </p>
                        </div>
                      </button>
                    ))}
                      </>
                    )}
                    
                    {/* Active Events */}
                    {activeEvents.length > 0 && (
                      <>
                        <div className="px-4 py-2 bg-green-50 text-sm font-medium text-green-800">
                          Published Events
                        </div>
                        {activeEvents.map((event) => (
                          <button
                            key={event.id}
                            onClick={() => setSelectedEvent(event)}
                            className={`w-full text-left p-4 hover:bg-gray-50 transition ${
                              selectedEvent?.id === event.id ? 'bg-blue-50' : ''
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h3 className="font-medium">{event.title}</h3>
                                <div className="flex items-center gap-2 mt-1">
                                  <p className="text-sm text-gray-600">
                                    {formatDate(event.date_time)}
                                  </p>
                                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getEventTypeBadgeStyle(event.event_type)}`}>
                                    {event.event_type.charAt(0).toUpperCase() + event.event_type.slice(1)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-3 mt-2 text-xs">
                              <span className="flex items-center gap-1 text-green-600">
                                <UserGroupIcon className="h-4 w-4" />
                                {event.approved_count} approved
                              </span>
                              <span className="text-yellow-600">
                                {event.pending_count} pending
                              </span>
                              {event.rejected_count > 0 && (
                                <span className="text-red-600">
                                  {event.rejected_count} rejected
                                </span>
                              )}
                            </div>
                            {event.invitation_sent_count > 0 && (
                              <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                                <EnvelopeIcon className="h-4 w-4" />
                                {event.invitation_sent_count} invitations sent
                              </div>
                            )}
                            <div className="mt-2">
                              <div className="flex justify-between text-xs text-gray-500 mb-1">
                                <span>Capacity</span>
                                <span>{event.capacity_percentage}% full</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-1.5">
                                <div 
                                  className={`h-1.5 rounded-full ${
                                    event.capacity_percentage >= 90 ? 'bg-red-600' : 
                                    event.capacity_percentage >= 70 ? 'bg-yellow-600' : 
                                    'bg-green-600'
                                  } w-[${Math.min(event.capacity_percentage, 100)}%]`}
                                />
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                {event.available_spots} spots available
                              </p>
                            </div>
                          </button>
                        ))}
                      </>
                    )}
                    
                    {/* Past Events - Only show when toggle is on */}
                    {showPastEvents && allPastEvents.length > 0 && (
                      <>
                        <div className="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-600">
                          Past Events
                        </div>
                        {allPastEvents.map((event) => (
                          <button
                            key={event.id}
                            onClick={() => setSelectedEvent(event)}
                            className={`w-full text-left p-4 hover:bg-gray-50 transition opacity-60 ${
                              selectedEvent?.id === event.id ? 'bg-blue-50' : ''
                            }`}
                          >
                            <h3 className="font-medium">{event.title}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-sm text-gray-600">
                                {formatDate(event.date_time)}
                              </p>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getEventTypeBadgeStyle(event.event_type)}`}>
                                {event.event_type.charAt(0).toUpperCase() + event.event_type.slice(1)}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Archived {event.archived_at ? formatDate(event.archived_at) : ''}
                            </p>
                          </button>
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Event Details & Tickets */}
          <div className="lg:col-span-2">
            {selectedEvent ? (
              <div className="space-y-8">
                <EventManager
                  event={selectedEvent}
                  onUpdate={handleEventUpdated}
                  onDelete={handleEventDeleted}
                  onArchive={handleEventArchived}
                  onRestore={handleEventRestored}
                  onInviteClick={() => setShowInviteModal(true)}
                  onExportClick={() => setShowExportDialog(true)}
                />
                <AttendeeManager 
                  event={selectedEvent} 
                  onUpdate={loadEvents}
                  showInviteModal={showInviteModal}
                  showExportDialog={showExportDialog}
                  onInviteModalChange={setShowInviteModal}
                  onExportDialogChange={setShowExportDialog}
                />
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                Select an event to view details
              </div>
            )}
          </div>
        </div>
      </div>

      {showCreateModal && (
        <CreateEventModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleEventCreated}
        />
      )}
    </div>
  );
};

export default OrganizerDashboard;